import type { OnBoardingStore } from "@/Modules/OnBoarding/Store";
import type { Language } from "@/Common/Models/Domain/Language";
import type { Choice } from "@/Modules/Models/Domain/OnBoarding";
import type { AreaApi } from "@/Modules/Models/Api/OnBoarding";
import { Source } from "@/Common/Models/Domain/Source";
import { ChoicesName } from "@/Modules/Models/Domain/OnBoarding";

import TimeZoneApiToTimeZoneTransformer from "@/Modules/Transformers/TimeZone";
import OnBoardingAPIConnector from "@/Services/TherapyApi/OnBoarding";

import { danger } from "@/Utils/Debug";
import { hasValue } from "@/Utils/Objects";

const timeZoneTransformer = TimeZoneApiToTimeZoneTransformer;

class SetOptionsDataActionHandler {
  constructor(
    private readonly onBoardingStore: OnBoardingStore,
    private readonly onBoardingApiConnector: OnBoardingAPIConnector = new OnBoardingAPIConnector()
  ) {}

  public async handle(language: Language) {
    try {
      const source = hasValue(this.onBoardingStore.b2bUser)
        ? this.onBoardingStore.b2bUser.source
        : Source.GENERAL;
      const configDataApi = await this.onBoardingApiConnector.getConfig(language, source);
      const areasApi = await this.onBoardingApiConnector.getAreas(language);

      // seteamos si tenemos opción de trial para el idioma.
      this.onBoardingStore.isTrial = configDataApi.trial;

      // seteamos los datos por cada componente dependiedo de lo que indique el parámetro
      // choicesName.
      // es decir, el choicesName puede ser "sub_area", "couple_area", "gender_preferenes".... que corresponde a los parámetros
      // que devuelve el api. (en los dos endpoints de arriba)
      // seteamos esos valores del api, después de transformarlos, dentro del componente en la variable
      // choices
      Object.keys(this.onBoardingStore.stepSource).map((stepSourceName) => {
        const stepSource =
          this.onBoardingStore.stepSource[stepSourceName as keyof typeof this.onBoardingStore.stepSource];

        if (stepSource?.components) {
          stepSource.components.forEach((component) => {
            if (component.choicesName) {
              if (component.choicesName === ChoicesName.MAIN_AREA) {
                return (component.choices = this.transformAreasData(areasApi));
              }

              if (
                component.choicesName === ChoicesName.SUB_AREA ||
                component.choicesName === ChoicesName.COUPLE_SUBAREA
              ) {
                return (component.choices = this.transformSubAreasData(areasApi));
              }

              // @ts-expect-error THER-6617
              component.choices = configDataApi[component.choicesName];
            }
          });
        }
      });

      this.onBoardingStore.locations = configDataApi.locations;
      this.onBoardingStore.companySections = configDataApi.company_sections;
      this.onBoardingStore.timeZones = configDataApi.time_zones.map(timeZoneTransformer.toDomain);
    } catch (e) {
      danger(e);
    }
  }
  private transformAreasData(areasApi: AreaApi[]): Choice[] {
    return areasApi
      .map((area) => {
        return {
          uuid: area.area_uuid,
          text: area.area_text,
          name: area.online_event_name,
          extra: area.extra,
          areaOrder: area.area_order,
        };
      })
      .sort((a, b) => a.areaOrder - b.areaOrder);
  }

  private transformSubAreasData(areasApi: AreaApi[]): Choice[] {
    return areasApi.reduce((acc: Choice[], currentArea: AreaApi) => {
      const subArea = currentArea.subareas.map((subArea) => {
        return {
          mainArea: currentArea.area_uuid,
          uuid: subArea.subarea_uuid,
          text: subArea.area_text,
          name: subArea.online_event_name,
          mainOnlineEventName: currentArea.online_event_name,
          isCouple: subArea.is_couple,
        };
      });

      acc.push(...subArea);

      return acc;
    }, []);
  }
}

export default SetOptionsDataActionHandler;
